import { useDispatch, useSelector } from "react-redux";
import { collator, ExecutiveDashboardUrl, FactoryPathUrl } from "../../utils/common";
import type { AppDispatch, RootState } from "../../store";
import { Select } from "antd";
import { EnvironmentOutlined, UserOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { clearUserInfo, clearWeatherInfo, setSelectedCustomerName, setSelectedSite, setSelectedTimezone } from "../../redux/slices/customerSlice";
import { clearGraphData, clearPowerState, clearGenChangeData, setSelectedGeneratorId, setSelectedSourceId } from "../../redux/slices/powerSlice";
import { clearDashboardData, clearGeneralData, clearPowerFuelData } from "../../redux/slices/dashboardSlice";
import { clearChargerData } from "../../redux/slices/chargerSlice";
import { fetchAllCustomerSites } from "../../redux/services/customerService";
import { useNavigate } from "react-router-dom";
import { DASHBOARD, FUEL, GENERAL, POWER_DIAG, POWER_GENERATION } from "../../utils/constants";
import { useEffect, useState } from "react";

const SelectDropdownCustSite = ({ isMobile, lastSegment, isNewIndustry }: { isMobile: boolean, lastSegment: string, isNewIndustry: boolean }) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { permittedCustomers, permittedCustomersLoading, customerSites, customerSitesLoading, selectedCustomerName, selectedSite } = useSelector((state: RootState) => state.userInfo);
    const { FactoryPath, ExecutivePath, selectedKey } = useSelector((state: RootState) => state.userInfo);
    const { selectedGeneratorId } = useSelector((state: RootState) => state.power);
    const { loadingGenDetailsForSite: isLoadingGeneratorList, generatorList: generatorListFromAPI, generatorListForMQTT } = useSelector((state: RootState) => state.dashboard);
    const isDisabled = permittedCustomers.length === 1 || permittedCustomersLoading || (customerSites.length === 0 && customerSitesLoading);
    const [generatorList, setGeneratorList] = useState<any[]>([]);

    useEffect(() => {
        if (selectedKey === POWER_DIAG) {
            if (generatorListForMQTT) {
                setGeneratorList(
                    generatorListForMQTT
                        .filter(
                            (info) =>
                                info.equipment_name !== null &&
                                !["EV Charger", "EV_Charger"].includes(info.equipment_name)
                        )
                        .map((info) => ({
                            value: info.equipment_id,
                            label: info.equipment_name,
                            source_id: info.source_id,
                        }))
                );
            }
        }
        else if (selectedKey === POWER_GENERATION) {
            if (generatorListFromAPI) {
                setGeneratorList(
                    generatorListFromAPI
                        .filter(
                            (info) =>
                                info.equipment_name !== null &&
                                !["EV Charger", "EV_Charger"].includes(info.equipment_name)
                        )
                        .map((info) => ({
                            value: info.equipment_id,
                            label: info.equipment_name,
                            source_id: info.source_id,
                        }))
                );
            }
        }
    }, [generatorListFromAPI, selectedKey]);
    const handleSelectCustomerChange = (value: string) => {
        dispatch(setSelectedCustomerName(value))
        dispatch(setSelectedTimezone(permittedCustomers.find(data => data.customer === value)?.preferences?.timezone || 'US/Pacific'));
        dispatch(clearUserInfo());
        dispatch(clearPowerState());
        dispatch(clearPowerFuelData());
        dispatch(clearGraphData());
        dispatch(clearWeatherInfo());
        dispatch(clearDashboardData());
        dispatch(clearChargerData());
        dispatch(fetchAllCustomerSites(value)).then((response: any) => {
            const sites = response?.payload || [];
            if (sites.length === 1) {
                dispatch(setSelectedSite(sites[0].name));
                navigate(`/${value}/${sites[0].name}/${GENERAL}`);
            } else if (!FactoryPathUrl(FactoryPath)) {
                dispatch(setSelectedSite(''));
                navigate(`/${value}/${DASHBOARD}`);
            }
        });

    }

    const handleSelectSiteChange = (value: string) => {
        dispatch(clearChargerData());
        dispatch(clearPowerState());
        dispatch(clearWeatherInfo());
        dispatch(clearWeatherInfo());
        dispatch(clearGeneralData());
        dispatch(clearPowerFuelData());
        dispatch(clearGraphData());
        dispatch(setSelectedSite(value));
        const selectedSiteObj = customerSites.find((data) => data.name === value)
        if (selectedSiteObj) {
            const present_components = selectedSiteObj?.preferences?.present_components;
            if (present_components.includes(lastSegment) && ![POWER_GENERATION, FUEL].includes(lastSegment)) {
                navigate(`/${selectedCustomerName}/${value}/${lastSegment}`);
            }
            else if (present_components.includes(lastSegment) && [POWER_GENERATION, FUEL].includes(lastSegment)) {
                // navigate(`/${selectedCustomerName}/${value}/${GENERAL}`);
            }
            else {
                navigate(`/${selectedCustomerName}/${value}/${GENERAL}`);
            }
        }
    }

    const handleEquipmentChange = (e: any) => {
        e && dispatch(clearGenChangeData());
        e && dispatch(setSelectedGeneratorId(e));
        e &&
            dispatch(
                setSelectedSourceId(
                    generatorList.find((item) => item.value === e)?.source_id
                ) || ""
            );
        e &&
            navigate(
                `/${selectedCustomerName}/${selectedSite}/${selectedKey}/${e}`
            );
    };

    return <div>
        {!isNewIndustry && <>
            {!isMobile && !(FactoryPathUrl(FactoryPath) || ExecutiveDashboardUrl(ExecutivePath)) && (
                <Select
                    value={selectedCustomerName}
                    loading={permittedCustomersLoading || (customerSites.length === 0 && customerSitesLoading)}
                    showSearch
                    virtual={false}
                    options={permittedCustomers.map((item) => ({ label: item.name, value: item.customer })).sort((a, b) => collator.compare(a.label, b.label))}
                    suffixIcon={<UserOutlined style={{ fontSize: '16px' }} className="clr-blk" />}
                    onChange={handleSelectCustomerChange}
                    disabled={isDisabled}
                    size="large"
                    className={isDisabled ? 'insideSelect select-disabled' : 'insideSelect'}
                    style={{ minWidth: '180px', flexShrink: 0 }}
                />
            )}
        </>}
        {!isNewIndustry && <>
            {!isMobile && !(FactoryPathUrl(FactoryPath) || ExecutiveDashboardUrl(ExecutivePath)) && selectedSite && (
                <Select
                    disabled={(customerSites.length === 0 && customerSitesLoading) || permittedCustomersLoading}
                    value={selectedSite}
                    showSearch
                    virtual={false}
                    size="large"
                    options={customerSites.map((item) => ({ label: item.name, value: item.name })).sort((a, b) => collator.compare(a.label, b.label))}
                    className="insideSelect"
                    suffixIcon={<EnvironmentOutlined style={{ fontSize: '16px' }} className="h-18 clr-blk" />}
                    onChange={handleSelectSiteChange}
                    style={{ minWidth: '180px', flexShrink: 0 }}
                />
            )}
        </>}
        {/* Generator Selector - Only show on Power page and non-mobile */}
        {!isNewIndustry && <>
            {!isMobile && !(FactoryPathUrl(FactoryPath) || ExecutiveDashboardUrl(ExecutivePath)) && selectedSite && lastSegment === POWER_GENERATION && ((generatorList && generatorList[0]) || isLoadingGeneratorList) && (
                <Select
                    value={selectedGeneratorId}
                    style={{ minWidth: '180px', flexShrink: 0 }}
                    virtual={false}
                    allowClear={false}
                    options={generatorList}
                    placeholder="Select a Generator"
                    onClear={() => {
                        dispatch(setSelectedGeneratorId(null));
                        dispatch(setSelectedSourceId(""));
                        navigate(
                            `/${selectedCustomerName}/${selectedSite}/${POWER_GENERATION}`
                        );
                    }}
                    loading={isLoadingGeneratorList}
                    onChange={handleEquipmentChange}
                    size="large"
                    className="insideSelect"
                    suffixIcon={<ThunderboltOutlined style={{ fontSize: '16px' }} className="h-18 clr-blk" />}
                />
            )}
        </>}
    </div>
}
export default SelectDropdownCustSite;